import { Injectable } from '@angular/core';
import { addHandler, updatedChannelEvent } from '../models/events';
import { ColorDeciderService, DefaultColorName } from './color-decider.service';
import { FlagsService } from './channel-flags.service';
import { FlagOption, SENSOR_FAILURE } from '../models/flags';
import { AlarmLevel } from '../models/channelType';

@Injectable({ providedIn: 'root' })
export class GaugeService {
    constructor(
        private colorDeciderService: ColorDeciderService,
        private flagsService: FlagsService
    ) {}

    private FLAG_UNDEFINED_COLOR = '#20e420';
    private GRAPH_UNDEFINED_COLOR = '#9cb2fb';
    private NORMAL_RANGE_KEYS = [FlagOption.NORMAL, 'lowToNormal', 'normalToHigh'];

    private ARC_WIDTH = 169;
    private ARC_HEIGHT = 143;
    private CENTER_X = 84.05;
    private CENTER_Y = 84;
    private OUTER_RADIUS = 84;
    private INNER_RADIUS = 73.3;
    private ARC_ANGLE = 133.4;

    private ARC_LEFT_OUTER = { x: 23.225, y: 141.634 };
    private ARC_LEFT_INNER = { x: 30.795, y: 134.071 };
    private ARC_LEFT_CAP = 'c.76.795 2.023.793 2.801.017l4.749-4.735c.782-.779.78-2.044.02-2.845';
    private ARC_RIGHT_OUTER = { x: 144.874, y: 141.635 };
    private ARC_RIGHT_INNER = { x: 137.306, y: 134.072 };
    private ARC_RIGHT_CAP = 'c-.761.801-.763 2.066.019 2.845l4.748 4.735c.779.776 2.041.778 2.801-.017';

    private PIN_POINTS = [
        [7.247, 41.002],
        [7.256, 41.106],
        [7.187, 41.186],
        [3.815, 45.098],
        [3.618, 45.326],
        [3.431, 45.09],
        [0.318, 41.178],
        [0.257, 41.102],
        [0.265, 41.002],
        [3.377, 0.023],
        [3.875, 0.022],
    ];
    private PIN_PIVOT_X = 3.8;
    private PIN_PIVOT_Y = 41;

    private PIN_TRANSITION = 'transform 0.5s ease-out';
    private PATH_DECIMALS = 3;
    private MINOR_TICKS = 5;
    private MAJOR_TICK = { from: 70, to: 64, width: 0.8 };
    private MINOR_TICK = { from: 70, to: 67, width: 0.5 };
    private LABEL_RADIUS = 58;
    private LABEL_FONT_SIZE = 5.5;
    private VALUE_Y = 115;
    private VALUE_FONT_SIZE = 13;
    private STATUS_Y = 129;
    private STATUS_FONT_SIZE = 9;
    private TITLE_FONT_SIZE = 12;
    private TITLE_LINE_HEIGHT = 15;
    private TITLE_GAP = 8;
    private FONT_FAMILY = 'NotoSans';

    private DEFAULT_ARC_COLOR = '#2c3a48';
    private DEFAULT_PIN_COLOR = '#5b6876';
    private TICK_COLOR = 'rgba(255, 255, 255, 0.4)';
    private VALUE_COLOR = '#ffffff';
    private SUB_TEXT_COLOR = 'rgba(255, 255, 255, 0.7)';

    private DEFAULT_WIDTH = 400;
    private DEFAULT_GAUGE_SCALE = 6;

    addGaugeListener(elem, channel, scaleValues: number[], decimalPlaces: number) {
        const handler = async (event: CustomEvent) => {
            const { value } = event.detail;
            if (isNaN(value)) return;
            this.updateValue(elem, channel, this.scaleValue(channel, value), scaleValues, decimalPlaces);
        };
        addHandler(elem, updatedChannelEvent, handler);
    }

    drawAndGetLiveInstance(channel, element) {
        if (!channel) return;
        const setting = this.getSetting(element, channel);
        const oldRect = element.querySelector('rect');
        const width = oldRect ? Number(oldRect.getAttribute('width')) : this.DEFAULT_WIDTH;
        const oldX = oldRect ? Number(oldRect.getAttribute('x')) : 0;
        const oldY = oldRect ? Number(oldRect.getAttribute('y')) : 0;

        Array.from(element.children).forEach(child => {
            if (child !== oldRect) element.removeChild(child);
        });

        const min = this.scaleValue(channel, channel.fcuData.measurableRange.min);
        const max = this.scaleValue(channel, channel.fcuData.measurableRange.max);
        const decimalPlaces = this.getDecimalPlaces(channel);
        const scaleValues =
            setting.isCustom && setting.scaleValues.length === setting.divisions + 2
                ? setting.scaleValues
                : this.getAutoScaleValues(min, max, setting.divisions + 1);
        const axisLineColors = this.buildAxisLineColors(channel, min, max);

        const content = this.createElement('g', {});
        element.appendChild(content);

        const body = this.createElement('g', {});
        content.appendChild(body);
        let previous = 0;
        axisLineColors.forEach(([percentage, color], i) => {
            const current = i === axisLineColors.length - 1 ? 1 : percentage;
            const from = this.valueToAngle(min + (max - min) * previous, scaleValues);
            const to = this.valueToAngle(min + (max - min) * current, scaleValues);
            body.appendChild(this.createElement('path', { d: this.buildArcPath(from, to), fill: color }));
            previous = current;
        });
        this.appendTicks(body, scaleValues, decimalPlaces);

        body.appendChild(
            this.createElement('path', {
                d: this.buildPinPath(),
                fill: this.DEFAULT_PIN_COLOR,
                style: `transform-origin: ${this.CENTER_X}px ${this.CENTER_Y}px; transform: rotate(${-this.ARC_ANGLE}deg); transition: ${this.PIN_TRANSITION}`,
                'data-gauge-pin': 'true',
            })
        );
        this.appendValueText(body, setting, channel.opsData.measurementUnit);

        const title = setting.title || channel.opsData?.name || '';
        const titleHeight = setting.nameDisplay && title ? this.appendTitle(content, title) : 0;
        const titleArea = titleHeight ? titleHeight + this.TITLE_GAP : 0;
        const isTop = setting.titlePosition === 'top';
        const titleText = content.querySelector('text[data-gauge-title]');
        if (isTop) body.setAttribute('transform', `translate(0 ${titleArea})`);
        titleText?.setAttribute('transform', `translate(0 ${isTop ? 0 : this.ARC_HEIGHT + this.TITLE_GAP})`);

        const ratio = width / this.ARC_WIDTH;
        oldRect?.setAttribute('height', String((this.ARC_HEIGHT + titleArea) * ratio));
        content.setAttribute('transform', `translate(${oldX} ${oldY}) scale(${ratio})`);

        this.addGaugeListener(element, channel, scaleValues, decimalPlaces);
    }

    private updateValue(element, channel, value: number, scaleValues: number[], decimalPlaces: number) {
        const status = this.flagsService.getStatusByRowId(channel.rowId);
        const level = this.flagsService.getLevelByRowId(channel.rowId);
        const statusColor =
            !status || status === FlagOption.NORMAL
                ? this.FLAG_UNDEFINED_COLOR
                : (this.colorDeciderService.getAlarmStateColor(status, channel)?.color ?? this.GRAPH_UNDEFINED_COLOR);

        const pin = element.querySelector('[data-gauge-pin]');
        if (pin) pin.style.transform = `rotate(${this.valueToAngle(value, scaleValues)}deg)`;
        pin?.setAttribute('fill', statusColor);

        const valueText = element.querySelector('[data-gauge-value]');
        if (valueText) {
            const setting = this.getSetting(element, channel);
            valueText.textContent = this.getValueText(
                setting,
                value.toFixed(decimalPlaces),
                channel.opsData.measurementUnit
            );
        }

        const statusText = element.querySelector('[data-gauge-status]');
        if (statusText) statusText.textContent = this.getStatusText(level);
    }

    private getStatusText(level): string {
        const levelMap = {
            [AlarmLevel.Caution]: DefaultColorName.CAUTION,
            [AlarmLevel.Warning]: DefaultColorName.WARNING,
            [AlarmLevel.Alarm]: DefaultColorName.ALARM,
        };
        if (level === SENSOR_FAILURE) return DefaultColorName.SENSOR_FAILURE;
        return levelMap[level] ?? FlagOption.NORMAL;
    }

    private getSetting(element, channel) {
        const scaleValues = element.getAttribute('gauge-scale-values');
        return {
            divisions: Number(element.getAttribute('gauge-divisions')) || this.getDefaultScaleDivision(channel) - 1,
            isCustom: element.getAttribute('gauge-custom-divisions') === 'true',
            scaleValues: scaleValues ? JSON.parse(scaleValues) : [],
            unitDisplay: element.getAttribute('gauge-unit-display') !== 'false',
            valueDisplay: element.getAttribute('gauge-value-display') !== 'false',
            nameDisplay: element.getAttribute('gauge-name-display') !== 'false',
            title: element.getAttribute('gauge-title') ?? '',
            titlePosition: element.getAttribute('gauge-title-position') ?? 'bottom',
        };
    }

    private getDecimalPlaces(channel): number {
        const scale = channel.fcuData.scale || 1;
        return scale === 1 ? 0 : Math.round(Math.log10(scale));
    }

    private getDefaultScaleDivision(channel): number {
        const range = channel?.fcuData?.measurableRange;

        if (range?.max != null && range?.min != null) {
            const scale = channel?.fcuData?.scale ?? 1;
            const channelRange = (channel.fcuData.measurableRange.max - channel.fcuData.measurableRange.min) / scale;

            for (let i = 7; i >= 3; i--) {
                const result = channelRange / i;
                const rounded = Number(result.toFixed(1));

                if (Math.abs(result - rounded) < 0.0000001) {
                    return i;
                }
            }
        }
        return this.DEFAULT_GAUGE_SCALE;
    }

    private scaleValue(channel, value: number) {
        return value / channel.fcuData.scale;
    }

    private buildAxisLineColors(channel, rangeMin: number, rangeMax: number): [number, string][] {
        const totalRange = Math.abs(rangeMax - rangeMin);
        const ranges = this.calculateRanges(channel, rangeMin, rangeMax);

        const colorArr: [number, string][] = [];
        let previewRange = 0;
        let rangeKeys = Array.from(ranges.keys());

        for (let i = 0; i < rangeKeys.length; i++) {
            let range = ranges.get(rangeKeys[i]);
            const width = range.max - range.min;
            if (width > 0) {
                let percentage = previewRange + (range.max - range.min) / totalRange;
                let color = this.colorDeciderService.getAlarmStateColor(rangeKeys[i], channel)?.color;
                if (this.NORMAL_RANGE_KEYS.includes(rangeKeys[i])) color = this.FLAG_UNDEFINED_COLOR;
                colorArr.push([percentage, color]);
                previewRange = percentage;
            }
        }

        return colorArr;
    }

    private calculateRanges(channel, rangeMin: number, rangeMax: number) {
        const fcuAlarms = channel.fcuData;
        const rangeOffset = 1 / channel.fcuData.scale;
        const rangeMap = new Map();

        const toRange = (min: number, max: number, zeroOut: boolean = false): { min: number; max: number } => {
            if (max - min == 0 && !zeroOut) max += rangeOffset;
            return zeroOut ? { min: 0, max: 0 } : { min, max };
        };

        const gapRange = (min: number, max: number): { min: number; max: number } => {
            if (max - min <= rangeOffset) return { min: 0, max: 0 };
            if (max != rangeMax) max -= rangeOffset;
            return { min, max };
        };

        const veryLow = toRange(
            rangeMin,
            this.scaleValue(channel, fcuAlarms.alarmSettingVeryLow.setPoint),
            !fcuAlarms.alarmSettingVeryLow.enable
        );
        const low = toRange(
            fcuAlarms.alarmSettingVeryLow.enable ? veryLow.max : rangeMin,
            this.scaleValue(channel, fcuAlarms.alarmSettingLow.setPoint),
            !fcuAlarms.alarmSettingLow.enable
        );
        const veryHigh = toRange(
            this.scaleValue(channel, fcuAlarms.alarmSettingVeryHigh.setPoint),
            rangeMax,
            !fcuAlarms.alarmSettingVeryHigh.enable
        );
        const high = toRange(
            this.scaleValue(channel, fcuAlarms.alarmSettingHigh.setPoint),
            fcuAlarms.alarmSettingVeryHigh.enable ? veryHigh.min : rangeMax,
            !fcuAlarms.alarmSettingHigh.enable
        );

        const hasNormalRange = fcuAlarms.normalRange.min !== 0 || fcuAlarms.normalRange.max !== 0;
        const normal = toRange(
            this.scaleValue(channel, fcuAlarms.normalRange.min),
            this.scaleValue(channel, fcuAlarms.normalRange.max),
            !hasNormalRange
        );
        const withoutNormalRange = ((high.min || rangeMax) - (low.max || rangeMin)) / 2;

        const lowToNormalGap = gapRange(
            low.max || rangeMin,
            hasNormalRange ? normal.min : (low.max || rangeMin) + withoutNormalRange
        );

        const normalToHighGap = gapRange(hasNormalRange ? normal.max : lowToNormalGap.max, high.min || rangeMax);

        rangeMap.set(FlagOption.LOW_LOW, veryLow);
        rangeMap.set(FlagOption.LOW, low);
        rangeMap.set('lowToNormal', lowToNormalGap);
        rangeMap.set(FlagOption.NORMAL, normal);
        rangeMap.set('normalToHigh', normalToHighGap);
        rangeMap.set(FlagOption.HIGH, high);
        rangeMap.set(FlagOption.HIGH_HIGH, veryHigh);

        return rangeMap;
    }

    private getAutoScaleValues(min: number, max: number, divisions: number): number[] {
        const step = (max - min) / divisions;
        return Array.from({ length: divisions + 1 }, (_, i) => {
            if (i === 0) return min;
            if (i === divisions) return max;
            return this.roundScaleValue(min + step * i, max, step);
        });
    }

    private roundScaleValue(value: number, max: number, step: number): number {
        if (!Number.isInteger(max) || value === 0) return value;
        let maxUnit = 1;
        while (max !== 0 && max % (maxUnit * 10) === 0) maxUnit *= 10;
        const topUnit = 10 ** Math.floor(Math.log10(Math.abs(value)));
        const stepUnit = 10 ** Math.floor(Math.log10(Math.abs(step)));
        const unit = Math.min(maxUnit, topUnit, stepUnit);
        return Math.sign(value) * Math.round(Math.abs(value) / unit) * unit;
    }

    private valueToAngle(value: number, scaleValues: number[]): number {
        const last = scaleValues.length - 1;
        if (value <= scaleValues[0]) return -this.ARC_ANGLE;
        if (value >= scaleValues[last]) return this.ARC_ANGLE;
        const index = scaleValues.findIndex((_, i) => i < last && value <= scaleValues[i + 1]);
        const ratio = (value - scaleValues[index]) / (scaleValues[index + 1] - scaleValues[index]);
        return -this.ARC_ANGLE + ((index + ratio) * this.ARC_ANGLE * 2) / last;
    }

    private buildArcPath(from: number, to: number): string {
        const isStart = from <= -this.ARC_ANGLE;
        const isEnd = to >= this.ARC_ANGLE;
        const largeArc = to - from > 180 ? 1 : 0;
        const outerFrom = isStart ? this.ARC_LEFT_OUTER : this.polar(from, this.OUTER_RADIUS);
        const outerTo = isEnd ? this.ARC_RIGHT_OUTER : this.polar(to, this.OUTER_RADIUS);
        const innerFrom = isStart ? this.ARC_LEFT_INNER : this.polar(from, this.INNER_RADIUS);
        const innerTo = isEnd ? this.ARC_RIGHT_INNER : this.polar(to, this.INNER_RADIUS);

        return [
            `M${outerTo.x} ${outerTo.y}`,
            `A${this.OUTER_RADIUS} ${this.OUTER_RADIUS} 0 ${largeArc} 0 ${outerFrom.x} ${outerFrom.y}`,
            isStart ? this.ARC_LEFT_CAP : `L${innerFrom.x} ${innerFrom.y}`,
            `A${this.INNER_RADIUS} ${this.INNER_RADIUS} 0 ${largeArc} 1 ${innerTo.x} ${innerTo.y}`,
            isEnd ? this.ARC_RIGHT_CAP : '',
            'Z',
        ].join('');
    }

    private buildPinPath(): string {
        const points = this.PIN_POINTS.map(
            ([x, y], i) =>
                `${i ? 'L' : 'M'}${this.round(this.CENTER_X + x - this.PIN_PIVOT_X)} ${this.round(this.CENTER_Y + y - this.PIN_PIVOT_Y)}`
        );
        return `${points.join('')}Z`;
    }

    private appendTicks(parent: SVGElement, scaleValues: number[], decimalPlaces: number): void {
        const divisions = scaleValues.length - 1;
        const tickCount = divisions * this.MINOR_TICKS;
        for (let i = 0; i <= tickCount; i++) {
            const isMajor = i % this.MINOR_TICKS === 0;
            const tick = isMajor ? this.MAJOR_TICK : this.MINOR_TICK;
            const angle = -this.ARC_ANGLE + (i * this.ARC_ANGLE * 2) / tickCount;
            const from = this.polar(angle, tick.from);
            const to = this.polar(angle, tick.to);
            parent.appendChild(
                this.createElement('line', {
                    x1: from.x,
                    y1: from.y,
                    x2: to.x,
                    y2: to.y,
                    stroke: this.TICK_COLOR,
                    'stroke-width': tick.width,
                })
            );
            if (!isMajor) continue;

            const labelPoint = this.polar(angle, this.LABEL_RADIUS);
            const label = this.createText(
                scaleValues[i / this.MINOR_TICKS].toFixed(decimalPlaces),
                labelPoint.y,
                this.LABEL_FONT_SIZE,
                this.TICK_COLOR
            );
            label.setAttribute('x', String(labelPoint.x));
            parent.appendChild(label);
        }
    }

    private appendValueText(parent: SVGElement, setting, unit: string): void {
        const valueText = this.getValueText(setting, '-', unit);
        if (valueText) {
            const text = this.createText(valueText, this.VALUE_Y, this.VALUE_FONT_SIZE, this.VALUE_COLOR);
            text.setAttribute('font-weight', '600');
            text.setAttribute('data-gauge-value', 'true');
            parent.appendChild(text);
        }
        if (setting.valueDisplay) {
            const text = this.createText('', this.STATUS_Y, this.STATUS_FONT_SIZE, this.SUB_TEXT_COLOR);
            text.setAttribute('data-gauge-status', 'true');
            parent.appendChild(text);
        }
    }

    private getValueText(setting, value: string, unit: string): string {
        return [setting.valueDisplay ? value : '', setting.unitDisplay ? unit : ''].filter(Boolean).join(' ');
    }

    private appendTitle(parent: SVGElement, title: string): number {
        const text = this.createText('', 0, this.TITLE_FONT_SIZE, this.SUB_TEXT_COLOR);
        text.setAttribute('data-gauge-title', 'true');
        parent.appendChild(text);

        let line = this.appendTitleLine(text, 0);
        title.split(' ').forEach(word => {
            const previous = line.textContent;
            line.textContent = previous ? `${previous} ${word}` : word;
            if (previous && line.getComputedTextLength() > this.ARC_WIDTH) {
                line.textContent = previous;
                line = this.appendTitleLine(text, text.childNodes.length);
                line.textContent = word;
            }
        });
        return text.childNodes.length * this.TITLE_LINE_HEIGHT;
    }

    private appendTitleLine(text: SVGTextElement, index: number): SVGTSpanElement {
        const line = this.createElement('tspan', {
            x: this.CENTER_X,
            y: this.TITLE_LINE_HEIGHT * index + this.TITLE_FONT_SIZE / 2,
        }) as SVGTSpanElement;
        text.appendChild(line);
        return line;
    }

    private createText(content: string, y: number, fontSize: number, color: string): SVGTextElement {
        const text = this.createElement('text', {
            x: this.CENTER_X,
            y,
            fill: color,
            'font-size': fontSize,
            'font-family': this.FONT_FAMILY,
            'text-anchor': 'middle',
            'dominant-baseline': 'central',
        }) as SVGTextElement;
        text.textContent = content;
        return text;
    }

    private createElement(tag: string, attributes: Record<string, string | number>): SVGElement {
        const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
        Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)));
        return element;
    }

    private polar(angle: number, radius: number): { x: number; y: number } {
        const radian = (angle * Math.PI) / 180;
        return {
            x: this.round(this.CENTER_X + radius * Math.sin(radian)),
            y: this.round(this.CENTER_Y - radius * Math.cos(radian)),
        };
    }

    private round(value: number): number {
        return Number(value.toFixed(this.PATH_DECIMALS));
    }
}
